import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import type {
  AgentSkill,
  AgentSkillBinding,
  InstallAgentSkillDto,
  CreateSkillBindingDto,
} from '../types/agent-skill';

// --- Registry ---

export function useAgentSkills(projectId?: string) {
  return useQuery<AgentSkill[]>({
    queryKey: ['agent-skills', { projectId }],
    queryFn: async () => {
      const params = projectId ? `?projectId=${projectId}` : '';
      const res = await api.get(`/api/agent-skills${params}`);
      return res.data;
    },
  });
}

export function useAgentSkill(id: string | undefined) {
  return useQuery<AgentSkill>({
    queryKey: ['agent-skills', id],
    queryFn: async () => {
      const res = await api.get(`/api/agent-skills/${id}`);
      return res.data;
    },
    enabled: !!id,
  });
}

export function useInstallAgentSkill(projectId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: InstallAgentSkillDto) => {
      const params = projectId ? `?projectId=${projectId}` : '';
      const res = await api.post(`/api/agent-skills/install${params}`, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-skills'] });
    },
  });
}

export function useDeleteAgentSkill() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/agent-skills/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-skills'] });
    },
  });
}

// --- Project bindings ---

export function useProjectSkillBindings(projectId: string | undefined) {
  return useQuery<AgentSkillBinding[]>({
    queryKey: ['project-skill-bindings', projectId],
    queryFn: async () => {
      const res = await api.get(`/api/projects/${projectId}/skills`);
      return res.data;
    },
    enabled: !!projectId,
  });
}

export function useBindProjectSkill(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateSkillBindingDto) => {
      const res = await api.post(`/api/projects/${projectId}/skills`, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['project-skill-bindings', projectId],
      });
    },
  });
}

export function useUnbindSkill(projectId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (bindingId: string) => {
      await api.delete(`/api/skill-bindings/${bindingId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['project-skill-bindings', projectId],
      });
    },
  });
}
